/**
 * The navigation IA, exactly as the design guide specifies it.
 *
 * The hero mockup shows eight flat destinations; the product has fifteen modules plus
 * settings, so the rail is grouped with small muted labels while the visual treatment
 * stays identical.
 *
 * `roles` mirrors the server's gate rather than replacing it. Hiding a destination is a
 * courtesy — the API refuses the request regardless, which is the actual boundary. Both
 * exist because a bookkeeper seeing a "רווחיות" link that 403s is a worse experience
 * than not seeing it, and a bookkeeper who can read profitability is a bug.
 */
import type { NavEntry, NavGroup } from "@onecpa/ui";

const OFFICE = ["owner", "manager", "staff"];
const MANAGEMENT = ["owner", "manager"];
const OWNER = ["owner"];

declare const __SHOW_DEV_LOGINS__: boolean;

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "ראשי",
    entries: [
      { id: "/", icon: "gauge", label: "לוח בקרה", roles: OFFICE },
      { id: "/clients", icon: "users", label: "לקוחות", roles: OFFICE },
    ],
  },
  {
    label: "דיווחים",
    entries: [
      { id: "/reporting", icon: "landmark", label: "דיווחים חודשיים", roles: OFFICE },
      { id: "/authority-reports", icon: "file-text", label: "שידור לרשויות", roles: OFFICE },
      { id: "/calendar", icon: "calendar-check", label: "לוח מועדים", roles: OFFICE },
    ],
  },
  {
    label: "תיקים",
    entries: [
      { id: "/annual", icon: "file-text", label: "דוחות שנתיים", roles: OFFICE },
      { id: "/wealth", icon: "landmark", label: "הצהרות הון", roles: OFFICE },
      { id: "/documents", icon: "file-text", label: "מסמכים", roles: OFFICE },
    ],
  },
  {
    label: "עבודה",
    entries: [
      { id: "/tasks", icon: "list-checks", label: "משימות", roles: OFFICE },
      { id: "/time", icon: "timer", label: "שעות", roles: OFFICE },
    ],
  },
  {
    label: "כספים",
    entries: [
      { id: "/billing", icon: "credit-card", label: "גבייה", roles: MANAGEMENT },
      // Owner-exclusive, per the persona pages: a bookkeeper never sees the margin on
      // their own book of clients.
      { id: "/profitability", icon: "trending-up", label: "רווחיות", roles: OWNER },
    ],
  },
  ...(__SHOW_DEV_LOGINS__
    ? ([
        {
          label: "פיתוח",
          entries: [
            { id: "/dev", icon: "timer", label: "קונסולת פיתוח", roles: OFFICE },
            { id: "/gallery", icon: "settings", label: "גלריית רכיבים", roles: OFFICE },
          ],
        },
      ] satisfies NavGroup[])
    : []),
  {
    label: "משרד",
    entries: [
      { id: "/office", icon: "gauge", label: "מבט משרד", roles: MANAGEMENT },
      { id: "/reminders", icon: "bell", label: "תזכורות", roles: OFFICE },
      { id: "/members", icon: "users", label: "עובדים והרשאות", roles: MANAGEMENT },
      { id: "/settings", icon: "settings", label: "הגדרות", roles: OFFICE },
    ],
  },
];

/** The five destinations the phone's bottom bar carries. */
export const MOBILE_ENTRIES: NavEntry[] = [
  { id: "/", icon: "gauge", label: "בקרה" },
  { id: "/clients", icon: "users", label: "לקוחות" },
  { id: "/reporting", icon: "landmark", label: "דיווחים" },
  { id: "/tasks", icon: "list-checks", label: "משימות" },
  { id: "/time", icon: "timer", label: "שעות" },
];

export const ROLE_LABELS: Record<string, string> = {
  owner: "בעל המשרד",
  manager: "מנהל משרד",
  staff: "מנהל חשבונות",
  client_user: "לקוח",
};

/**
 * Which rail entry a path belongs to.
 *
 * Longest-prefix rather than exact, so `/clients/:id` keeps "לקוחות" highlighted. An
 * active state that disappears on a detail page makes the rail feel broken.
 */
export function activeNavId(pathname: string): string {
  const candidates = NAV_GROUPS.flatMap((group) => group.entries.map((entry) => entry.id));
  const matches = candidates
    .filter((id) => id !== "/" && pathname.startsWith(id))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? "/";
}
